/**
 * behaviorMixin.js - Vue mixin integrujący BehaviorTracker z komponentami formularza
 * 
 * AUTOMATYCZNIE hookuje się w metody DynamicForm (existCheck, onSubmit, 
 * sendVerifyCodes, checkVerifyCodes, postKontoxData, verifyCode, routeToStage)
 * bez konieczności modyfikacji istniejących komponentów.
 * 
 * Dodatkowo śledzi:
 * - zmianę kroków (DynamicForm.activeStep)
 * - zmiany kalkulatora (CalcSlider.loanAmount)
 * - błędy walidacji
 * - login/logout
 * - TransactionLink / Authologic
 * 
 * Użycie w main.js:
 *   import behaviorMixin from '@/behaviorMixin';
 *   Vue.mixin(behaviorMixin);
 * 
 * ZERO zmian w DynamicForm.vue, CalcSlider.vue, FingoPage.vue itd.
 */

import BehaviorTracker from '@/behaviorTracker';


/**
 * Owija istniejącą metodę komponentu - wywołuje hook PRZED oryginalną metodą.
 * Bezpieczne: jeśli metoda nie istnieje, nic się nie dzieje.
 * 
 * @param {Vue} vm - instancja Vue
 * @param {string} methodName - nazwa metody do owinięcia
 * @param {Function} beforeHook - fn(args...) wywoływany przed oryginałem
 */
function wrapMethod(vm, methodName, beforeHook) {
  var original = vm[methodName];
  if (typeof original !== 'function') return;

  vm[methodName] = function () {
    try {
      beforeHook.apply(vm, arguments);
    } catch (e) {
      // hook nie powinien przerywać oryginalnej metody
    }
    return original.apply(vm, arguments);
  };
}


export default {

  created: function () {

    // ============================
    // INICJALIZACJA - FingoPage
    // ============================
    if (this.$options.name === 'FingoPage') {
      BehaviorTracker.init();
      // Stealth: krótki, nieopisowy alias globalny zamiast `window.BehaviorTracker`
      // (poprzednio nazwa zdradzała cel w konsoli dewelopera/fraudstera)
      window.__bt = BehaviorTracker;
      this.log('tracker initialized');

      // OTP registerlink: formularz wypełniany w FingoPage.mounted()
      // nie przechodzi przez DynamicForm.fillForm() więc trzeba osobno obsłużyć
      this.$watch('isRegisterLink', function (newVal) {
        if (newVal === true) {
          // OTP prefill nadchodzi - snapshot pre-form i reset
          BehaviorTracker.sendSnapshot('pre_form_otp');
          BehaviorTracker.reset();
          BehaviorTracker.onFormPrefilled([]);
        }
      });
    }

    // ============================
    // TransactionLink
    // ============================
    if (this.$options.name === 'TransactionlinkView') {
      BehaviorTracker.sendSnapshot('transactionlink_start');
    }

    // ============================
    // Authologic
    // ============================
    if (this.$options.name === 'AuthologicReturn') {
      BehaviorTracker.sendSnapshot('authologic_return');
    }

    // ============================
    // DynamicForm - wrapping metod
    // ============================
    if (this.$options.name === 'DynamicForm') {

      var vm = this;

      // --- existCheck ---
      // kluczowy moment: user podał dane kroku 1 i klikamy DALEJ
      // snapshot tutaj daje pełen obraz zachowania na kroku 1
      wrapMethod(vm, 'existCheck', function (phone, email, pesel) {
        BehaviorTracker.sendSnapshot('exist_check');
        BehaviorTracker.onSubmitAttempt('existCheck', !vm.$v || !vm.$v.form.$invalid);
      });

      // --- onSubmit ---
      // główny submit formularza na każdy endpoint (application, etc.)
      wrapMethod(vm, 'onSubmit', function (endpoint) {
        var ep = endpoint || 'application';
        BehaviorTracker.onSubmitAttempt(ep, !vm.$v || !vm.$v.form.$invalid);

        // ustaw fazę procesu na podstawie loan_id
        var hasLoanId = !!(vm.form && vm.form.loan_id);
        if (hasLoanId) {
          BehaviorTracker.setApplicationPhase('continuation', true);
        }

        BehaviorTracker.sendSnapshot('submit_' + ep);
      });

      // --- sendVerifyCodes ---
      // user przechodzi do weryfikacji SMS - snapshot zachowania do tego momentu
      wrapMethod(vm, 'sendVerifyCodes', function (phone, email) {
        BehaviorTracker.sendSnapshot('send_verify_codes');
      });

      // --- checkVerifyCodes ---
      // user wpisał kod i zatwierdza
      wrapMethod(vm, 'checkVerifyCodes', function (pcode, ecode) {
        BehaviorTracker.sendSnapshot('check_verify_codes');
      });

      // --- verifyCode ---
      // alternatywna weryfikacja kodu (stary flow)
      wrapMethod(vm, 'verifyCode', function () {
        BehaviorTracker.sendSnapshot('verify_code');
      });

      // --- postKontoxData ---
      // user zakończył Kontomatik i wysyłamy dane bankowe
      wrapMethod(vm, 'postKontoxData', function (kontoxData) {
        BehaviorTracker.sendSnapshot('kontox_submit');
      });

      // --- routeToStage ---
      // finalne przekierowanie - snapshot z kompletnym profilem behawioralnym
      wrapMethod(vm, 'routeToStage', function (stage, data) {
        // po weryfikacji (transactionlink, kontox, authologic) następny submit to resubmit
        if (/transactionlink|kontox|proposal/.test(stage)) {
          BehaviorTracker.setApplicationPhase('resubmit_after_verification', !!(vm.form && vm.form.loan_id));
        }
        BehaviorTracker.sendSnapshot('route_' + (stage || 'unknown'));
      });

      // --- onWaiting ---
      // polling start - ile czasu minęło od submita do rozpoczęcia czekania
      wrapMethod(vm, 'onWaiting', function (endpoint) {
        BehaviorTracker.sendSnapshot('waiting_start_' + (endpoint || 'application'));
      });

      // --- next ---
      // kliknięcie DALEJ (orchestruje existCheck + submit + verify)
      wrapMethod(vm, 'next', function () {
        BehaviorTracker.sendSnapshot('next_clicked_step_' + vm.activeStep);
      });

      // --- prev ---
      // kliknięcie COFNIJ
      wrapMethod(vm, 'prev', function () {
        BehaviorTracker.sendSnapshot('prev_clicked_step_' + vm.activeStep);
      });

      // --- fillForm ---
      // programowe wypełnienie formularza dla zalogowanego klienta
      // 1) wysyłamy snapshot pre-form (zachowujemy dane z logowania/nawigacji)
      // 2) reset BT - czysta baza tylko dla fazy formularza
      // 3) oznaczamy kontekst auth + prefill
      wrapMethod(vm, 'fillForm', function () {
        var page = vm.$root && vm.$root.mainpage;
        var isLoggedIn = page && page.loggedIn;

        // zachowaj dane pre-form
        BehaviorTracker.sendSnapshot('pre_form_phase');

        // reset - od teraz zbieramy TYLKO dane z formularza
        BehaviorTracker.reset();

        // kontekst
        if (isLoggedIn) {
          BehaviorTracker.setAuthenticated(true);
        }

        // zbierz nazwy pól z fmap
        var fieldNames = [];
        if (vm.fmap && typeof vm.fmap === 'object') {
          fieldNames = Object.keys(vm.fmap);
        }

        BehaviorTracker.onFormPrefilled(fieldNames);

        // ustaw fazę: 
        // - jeśli loan_id istnieje → kontynuacja/resubmit
        // - jeśli zalogowany user bez loan_id → też kontynuacja (powracający klient)
        //   bo formularz jest prefilled i user tylko klika DALEJ
        var hasLoanId = !!(vm.form && vm.form.loan_id);
        if (hasLoanId) {
          // jeśli phase jest już 'resubmit_after_verification' nie nadpisuj
          var currentPhase = BehaviorTracker.getSnapshot().context.applicationPhase;
          if (currentPhase !== 'resubmit_after_verification') {
            BehaviorTracker.setApplicationPhase('continuation', true);
          }
        } else if (isLoggedIn) {
          // zalogowany user, prefilled form, ale jeszcze bez loan_id
          // to normalny powracający klient — nie nowy wniosek
          BehaviorTracker.setApplicationPhase('continuation', false);
        }
      });
    }

    // ============================
    // CalcSlider - wrap goApplicate
    // ============================
    if (this.$options.name === 'CalcSlider') {
      wrapMethod(this, 'goApplicate', function () {
        BehaviorTracker.sendSnapshot('calc_go_applicate');
      });
    }

    // ============================
    // FingoClientView - klient wraca po kolejną pożyczkę
    // ============================
    if (this.$options.name === 'FingoClientView') {
      wrapMethod(this, 'nextLoan', function () {
        BehaviorTracker.sendSnapshot('client_next_loan');
      });
    }
  },


  watch: {

    // Śledzenie zmiany kroku w DynamicForm
    activeStep: function (newVal, oldVal) {
      if (this.$options.name === 'DynamicForm' && newVal !== oldVal) {
        BehaviorTracker.onStepChange(newVal, oldVal);
      }
    },

    // Śledzenie zmiany kwoty w CalcSlider
    loanAmount: function (newVal, oldVal) {
      if (this.$options.name === 'CalcSlider' && newVal !== oldVal) {
        BehaviorTracker.onCalcChange(newVal);
      }
    },

    // Śledzenie błędów formularza w DynamicForm
    formErrors: function (newVal) {
      if (this.$options.name === 'DynamicForm' && newVal === true) {
        BehaviorTracker.onValidationError(this.invalidFields || []);
      }
    },

    // Śledzenie logowania
    loggedIn: function (newVal, oldVal) {
      if (this.$options.name === 'FingoPage') {
        if (newVal && !oldVal) {
          BehaviorTracker.setAuthenticated(true);
          BehaviorTracker.sendSnapshot('user_logged_in');
        }
        if (!newVal && oldVal) {
          BehaviorTracker.sendSnapshot('user_logged_out');
          BehaviorTracker.setAuthenticated(false);
          BehaviorTracker.reset();
        }
      }
    }
  },


  methods: {

    /**
     * Pobiera snapshot behawioralny jako JSON string
     * do dołączenia do payloadu submit
     * @returns {string} JSON string
     */
    bt_getPayload: function () {
      if (!window.__bt) return '{}';
      try {
        return JSON.stringify(BehaviorTracker.getSnapshot());
      } catch (e) {
        return '{}';
      }
    },

    /**
     * USUNIĘTE: scoring na froncie (bt_getRecommendation / bt_getRiskScore).
     *
     * Ocena ryzyka jest liczona WYŁĄCZNIE po stronie backendu
     * (SeraphinScorer.php), z surowych snapshotów napływających do /log.
     * Front jest czystym kolektorem — wysyła dane behawioralne, nic nie liczy.
     * Backend wstrzykuje gotową rekomendację do custom['-23'] w
     * IndexController::application() przed forwardem do GATEWAY.
     */
  },


  beforeDestroy: function () {
    if (this.$options.name === 'FingoPage') {
      BehaviorTracker.sendSnapshot('page_destroy');
      BehaviorTracker.destroy();
    }
  }

};


// WEBPACK FOOTER //
// ./src/mixins/behaviorMixin.js